import { useEffect } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import type { TipTapDoc } from '../dataProvider';
import { cmsExtensions } from './cmsModel';

/**
 * The TipTap rich-text editor (D-cms-pages-core), the FIRST rich-text editor in the
 * build. It authors the FOCUSED feature set (headings h2 / h3, bold, italic, link,
 * bullet / ordered lists, image, the cta node) and NOTHING more: the toolbar exposes
 * exactly those, and the extension schema (cmsModel.cmsExtensions) rejects any other
 * node, so an unsupported block (a table, a column) cannot be authored. The value is
 * the canonical ProseMirror JSON (TipTapDoc); edits flow editor -> onChange(getJSON).
 *
 * SANITIZATION: the extension schema is the primary guard (only the configured nodes /
 * marks are valid). Link hrefs and image srcs are admin-authored; the eventual render-
 * side sanitization is the marketing site's concern (the swap seam).
 */
export const RichTextEditor = ({
  value,
  onChange,
}: {
  value: TipTapDoc;
  onChange: (doc: TipTapDoc) => void;
}) => {
  const editor = useEditor({
    extensions: cmsExtensions,
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getJSON() as TipTapDoc),
  });

  // Emit the canonical JSON once the editor has parsed the initial content, so the form
  // holds the normalized body (and a save with no edits still round-trips the doc).
  useEffect(() => {
    if (editor) onChange(editor.getJSON() as TipTapDoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;

  const tb = (
    testId: string,
    label: string,
    onClick: () => void,
    active = false,
  ) => (
    <Button
      size="small"
      variant={active ? 'contained' : 'outlined'}
      onClick={onClick}
      data-testid={testId}
    >
      {label}
    </Button>
  );

  const addLink = () => {
    const href = window.prompt('Link URL') ?? '';
    if (href) editor.chain().focus().setLink({ href }).run();
    else editor.chain().focus().unsetLink().run();
  };
  const addImage = () => {
    const src = window.prompt('Image URL') ?? '';
    if (src) editor.chain().focus().setImage({ src, alt: '' }).run();
  };
  const addCta = () => {
    const label = window.prompt('Button label') ?? 'Learn more';
    const href = window.prompt('Button link') ?? '';
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'cta',
        attrs: { href, tone: 'primary' },
        content: [{ type: 'text', text: label }],
      })
      .run();
  };

  return (
    <Box data-testid="cms-editor">
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1 }}
      >
        {tb(
          'cms-tb-h2',
          'H2',
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          editor.isActive('heading', { level: 2 }),
        )}
        {tb(
          'cms-tb-h3',
          'H3',
          () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          editor.isActive('heading', { level: 3 }),
        )}
        {tb(
          'cms-tb-bold',
          'Bold',
          () => editor.chain().focus().toggleBold().run(),
          editor.isActive('bold'),
        )}
        {tb(
          'cms-tb-italic',
          'Italic',
          () => editor.chain().focus().toggleItalic().run(),
          editor.isActive('italic'),
        )}
        {tb('cms-tb-link', 'Link', addLink, editor.isActive('link'))}
        {tb(
          'cms-tb-bullet',
          'Bullet list',
          () => editor.chain().focus().toggleBulletList().run(),
          editor.isActive('bulletList'),
        )}
        {tb(
          'cms-tb-ordered',
          'Numbered list',
          () => editor.chain().focus().toggleOrderedList().run(),
          editor.isActive('orderedList'),
        )}
        {tb('cms-tb-image', 'Image', addImage)}
        {tb('cms-tb-cta', 'CTA', addCta)}
      </Stack>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          minHeight: 160,
          '& .ProseMirror': { outline: 'none', minHeight: 140 },
          '& .cms-cta': {
            display: 'inline-block',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            textDecoration: 'none',
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};
